/**
 * Brand Ambassador apply form draft persistence (sessionStorage).
 * Survives refresh; intentionally does not survive tab close.
 * Never persists password, credit-card / Stripe fields, terms, or marketing
 * opt-ins (SMS / email) — consent must be given fresh on the BA form.
 */

import type { AmbassadorFormValues } from '@/components/form/SignupFormAmbassador/SignupForm';
import type { SignupState } from '@/types/Signup';

export const BA_APPLY_DRAFT_KEY = 'ba_apply_draft_v1';

/** Ignore drafts older than 24h (cheap insurance; sessionStorage usually dies with the tab). */
export const BA_APPLY_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

const STRING_ALLOWLIST = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'referralCode',
  'birthday',
  'instagram',
  'tiktok',
  'otherSocial',
  'ambassadorPitch',
  'address',
  'aptUnitSuite',
  'city',
  'state',
  'postalCode',
  'billingAddress',
  'billingAptUnitSuite',
  'billingCity',
  'billingState',
  'billingPostalCode'
] as const satisfies ReadonlyArray<keyof AmbassadorFormValues>;

const BOOLEAN_ALLOWLIST = [
  'sameAsShippingAddress'
] as const satisfies ReadonlyArray<keyof AmbassadorFormValues>;

/** Explicit deny — never written even if somehow present on the input object. */
const DENY_LIST = [
  'password',
  'stripePaymentMethodTokenId',
  'creditCardLast4',
  'expirationDate',
  'stripePaymentMethodBrand',
  'termsAgreement',
  // Fresh consent on BA form / submit — do not hand off or restore
  'smsOptIn',
  'marketingOptIn'
] as const satisfies ReadonlyArray<keyof AmbassadorFormValues>;

export type AmbassadorApplyDraftMembership = {
  subscription_level?: string;
  insurance_level?: string;
  addon_subscription?: string;
  planSlug?: string;
};

export type AmbassadorApplyDraftV1 = {
  v: 1;
  updatedAt: number;
  fields: Partial<AmbassadorFormValues>;
  membership: AmbassadorApplyDraftMembership;
};

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof sessionStorage !== 'undefined';
}

/**
 * Strip to allowlisted draft fields only. Card, password, and terms never survive.
 */
export function pickAmbassadorDraftFields(
  values: Partial<AmbassadorFormValues>
): Partial<AmbassadorFormValues> {
  const out: Partial<AmbassadorFormValues> = {};

  for (const key of STRING_ALLOWLIST) {
    const value = values[key];
    if (typeof value === 'string') {
      out[key] = value;
    }
  }

  for (const key of BOOLEAN_ALLOWLIST) {
    const value = values[key];
    if (typeof value === 'boolean') {
      out[key] = value;
    }
  }

  for (const key of DENY_LIST) {
    delete out[key];
  }

  return out;
}

/**
 * Map overlapping SignupContext fields into BA form shape.
 * Omits password, payment fields, and marketing opt-ins (SMS / email).
 */
export function seedFromSignupState(
  state: Partial<SignupState>
): Partial<AmbassadorFormValues> {
  const out: Partial<AmbassadorFormValues> = {};

  const copyString = (
    from: keyof SignupState,
    to: keyof AmbassadorFormValues = from as keyof AmbassadorFormValues
  ) => {
    const value = state[from];
    if (typeof value === 'string' && value.trim() !== '') {
      (out as Record<string, string>)[to] = value;
    }
  };

  copyString('firstName');
  copyString('lastName');
  copyString('email');
  copyString('phone');
  copyString('referralCode');
  copyString('birthday');
  copyString('instagram');
  copyString('tiktok');
  copyString('otherSocial');
  copyString('ambassadorPitch');
  copyString('address');
  copyString('aptUnitSuite');
  copyString('city');
  copyString('state');
  copyString('postalCode');
  copyString('billingAddress');
  copyString('billingAptUnitSuite');
  copyString('billingCity');
  copyString('billingState');
  copyString('billingPostalCode');

  if (typeof state.sameAsShippingAddress === 'boolean') {
    out.sameAsShippingAddress = state.sameAsShippingAddress;
  }

  return pickAmbassadorDraftFields(out);
}

export function membershipFromSignupState(
  state: Partial<SignupState>
): AmbassadorApplyDraftMembership {
  const planSlug = state.plan?.slug || state.subscription_level || undefined;
  return {
    planSlug: planSlug || undefined,
    subscription_level: state.subscription_level || planSlug || undefined,
    insurance_level: state.insurance_level || undefined,
    addon_subscription: state.addon_subscription || undefined
  };
}

/**
 * Build Formik initial values: empty defaults → context seed (fill gaps) → draft (wins).
 * Always lands on form view (caller must not restore confirm / tokens / terms).
 */
export function buildInitialAmbassadorFormValues(
  empty: AmbassadorFormValues,
  draftFields?: Partial<AmbassadorFormValues>,
  contextSeed?: Partial<AmbassadorFormValues>
): AmbassadorFormValues {
  const result: AmbassadorFormValues = { ...empty };

  const applyStrings = (
    source: Partial<AmbassadorFormValues> | undefined,
    mode: 'fillEmpty' | 'overwrite'
  ) => {
    if (!source) return;
    for (const key of STRING_ALLOWLIST) {
      const incoming = source[key];
      if (typeof incoming !== 'string' || incoming === '') continue;
      if (mode === 'overwrite' || !result[key]) {
        result[key] = incoming;
      }
    }
  };

  const applyBooleans = (
    source: Partial<AmbassadorFormValues> | undefined,
    mode: 'fillEmpty' | 'overwrite'
  ) => {
    if (!source) return;
    for (const key of BOOLEAN_ALLOWLIST) {
      const incoming = source[key];
      if (typeof incoming !== 'boolean') continue;
      if (mode === 'overwrite' || key === 'sameAsShippingAddress') {
        result[key] = incoming;
      }
    }
  };

  const safeContext = pickAmbassadorDraftFields(contextSeed || {});
  const safeDraft = pickAmbassadorDraftFields(draftFields || {});

  applyStrings(safeContext, 'fillEmpty');
  applyBooleans(safeContext, 'fillEmpty');
  applyStrings(safeDraft, 'overwrite');
  applyBooleans(safeDraft, 'overwrite');

  // Hard guarantees — never restore secrets / confirm / marketing consent
  result.password = '';
  result.stripePaymentMethodTokenId = '';
  result.creditCardLast4 = '';
  result.expirationDate = '';
  result.stripePaymentMethodBrand = '';
  result.termsAgreement = false;
  result.smsOptIn = empty.smsOptIn;
  result.marketingOptIn = empty.marketingOptIn;

  return result;
}

function parseDraft(raw: string | null): AmbassadorApplyDraftV1 | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as AmbassadorApplyDraftV1;
    if (!parsed || parsed.v !== 1 || typeof parsed.updatedAt !== 'number') {
      return null;
    }
    if (Date.now() - parsed.updatedAt > BA_APPLY_DRAFT_TTL_MS) {
      return null;
    }
    return {
      v: 1,
      updatedAt: parsed.updatedAt,
      fields: pickAmbassadorDraftFields(parsed.fields || {}),
      membership:
        parsed.membership && typeof parsed.membership === 'object'
          ? {
              subscription_level: parsed.membership.subscription_level,
              insurance_level: parsed.membership.insurance_level,
              addon_subscription: parsed.membership.addon_subscription,
              planSlug: parsed.membership.planSlug
            }
          : {}
    };
  } catch {
    return null;
  }
}

export function loadAmbassadorApplyDraft(): AmbassadorApplyDraftV1 | null {
  if (!isBrowser()) return null;
  try {
    const draft = parseDraft(sessionStorage.getItem(BA_APPLY_DRAFT_KEY));
    if (!draft) {
      // Corrupt or expired — drop the key so we don't keep failing
      sessionStorage.removeItem(BA_APPLY_DRAFT_KEY);
      return null;
    }
    return draft;
  } catch {
    return null;
  }
}

export function saveAmbassadorApplyDraft(
  fields: Partial<AmbassadorFormValues>,
  membership?: AmbassadorApplyDraftMembership
): void {
  if (!isBrowser()) return;
  try {
    let existing: AmbassadorApplyDraftV1 | null = null;
    try {
      existing = parseDraft(sessionStorage.getItem(BA_APPLY_DRAFT_KEY));
    } catch {
      existing = null;
    }

    const next: AmbassadorApplyDraftV1 = {
      v: 1,
      updatedAt: Date.now(),
      fields: pickAmbassadorDraftFields(fields),
      membership: membership ?? existing?.membership ?? {}
    };

    sessionStorage.setItem(BA_APPLY_DRAFT_KEY, JSON.stringify(next));
  } catch {
    // Private mode / quota — fail soft
  }
}

export function saveAmbassadorApplyMembership(
  membership: AmbassadorApplyDraftMembership
): void {
  if (!isBrowser()) return;
  try {
    let existing: AmbassadorApplyDraftV1 | null = null;
    try {
      existing = parseDraft(sessionStorage.getItem(BA_APPLY_DRAFT_KEY));
    } catch {
      existing = null;
    }

    const next: AmbassadorApplyDraftV1 = {
      v: 1,
      updatedAt: Date.now(),
      fields: existing?.fields ?? {},
      membership: {
        ...(existing?.membership ?? {}),
        ...membership
      }
    };

    sessionStorage.setItem(BA_APPLY_DRAFT_KEY, JSON.stringify(next));
  } catch {
    // fail soft
  }
}

export function clearAmbassadorApplyDraft(): void {
  if (!isBrowser()) return;
  try {
    sessionStorage.removeItem(BA_APPLY_DRAFT_KEY);
  } catch {
    // fail soft
  }
}

/**
 * One-shot handoff when leaving regular apply for BA.
 * Writes a snapshot of regular-apply state into the BA draft — does not continuously
 * sync regular Formik into BA. Snapshot fields win over any prior BA draft.
 */
export function writeAmbassadorHandoffFromSignupState(
  state: Partial<SignupState>
): void {
  if (!isBrowser()) return;

  const existing = loadAmbassadorApplyDraft();
  const handoffFields = seedFromSignupState(state);
  const handoffMembership = membershipFromSignupState(state);

  // Snapshot wins; prior BA draft only fills gaps (e.g. pitch typed on an earlier BA visit)
  // Low chance a BA application is already in progress, but account fo it anyway
  const mergedFields = buildInitialAmbassadorFormValues(
    // Use a minimal empty shell — only allowlisted keys matter
    {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      referralCode: '',
      birthday: '',
      smsOptIn: false,
      marketingOptIn: true,
      instagram: '',
      tiktok: '',
      otherSocial: '',
      ambassadorPitch: '',
      creditCardLast4: '',
      expirationDate: '',
      stripePaymentMethodTokenId: '',
      stripePaymentMethodBrand: '',
      address: '',
      aptUnitSuite: '',
      city: '',
      state: '',
      postalCode: '',
      sameAsShippingAddress: true,
      billingAddress: '',
      billingAptUnitSuite: '',
      billingCity: '',
      billingState: '',
      billingPostalCode: '',
      termsAgreement: false
    },
    handoffFields,
    existing?.fields
  );

  const existingMembership = existing?.membership ?? {};
  const membership: AmbassadorApplyDraftMembership = {
    planSlug: handoffMembership.planSlug || existingMembership.planSlug,
    subscription_level:
      handoffMembership.subscription_level ||
      existingMembership.subscription_level,
    insurance_level:
      handoffMembership.insurance_level || existingMembership.insurance_level,
    addon_subscription:
      handoffMembership.addon_subscription ||
      existingMembership.addon_subscription
  };

  saveAmbassadorApplyDraft(mergedFields, membership);
}
